import * as React from "react";

import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import { Checkbox } from "@src/lib/ui/Checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@src/lib/ui/Command";
import Spinner from "@src/lib/ui/Spinner";

export type MultiSelectMenuOption = {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  avatar?: React.ReactNode;
};

// Search only earns its row once the list is longer than a glance.
export const MULTI_SELECT_MENU_SEARCH_THRESHOLD = 8;

type MultiSelectMenuProps = {
  options: MultiSelectMenuOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  // Omit to hide "Select all" in the empty footer.
  onSelectAll?: () => void;
  // Defaults to showing search past MULTI_SELECT_MENU_SEARCH_THRESHOLD options.
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (term: string) => void;
  // Backspace in an empty search field.
  onRemoveLast?: () => void;
  isLoading?: boolean;
  hideFooter?: boolean;
  className?: string;
};

/**
 * The value list a multi-select opens: a borderless search row, the values
 * that were selected when the menu opened pinned under "Selected", checkbox
 * rows on the shared menu row chrome, and an "n selected · Clear" footer
 * ("Select all" while nothing is selected). Mount it only while open (inside
 * PopoverContent): the pinned group is captured on mount, so toggling rows
 * never reorders the list under the pointer.
 */
export function MultiSelectMenu({
  options,
  selectedValues,
  onToggle,
  onClear,
  onSelectAll,
  searchable,
  searchPlaceholder = "Search",
  onSearchChange,
  onRemoveLast,
  isLoading = false,
  hideFooter = false,
  className,
}: MultiSelectMenuProps): React.ReactElement {
  const [pinnedValues] = React.useState<string[]>(() => selectedValues);
  const [search, setSearch] = React.useState("");
  const commandRef = React.useRef<HTMLDivElement>(null);
  const showSearch =
    searchable ?? options.length > MULTI_SELECT_MENU_SEARCH_THRESHOLD;

  const selected = React.useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );
  const optionsByValue = React.useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );
  const pinnedOptions = search
    ? []
    : pinnedValues.flatMap((value) => {
        const option = optionsByValue.get(value);
        return option ? [option] : [];
      });
  const pinnedSet = new Set(pinnedOptions.map((option) => option.value));
  const restOptions = options.filter((option) => !pinnedSet.has(option.value));

  // Without a search field the Command root holds focus, so cmdk's ↑ ↓ Enter
  // still work; Space toggles too.
  React.useEffect(() => {
    if (!showSearch) commandRef.current?.focus({ preventScroll: true });
  }, [showSearch]);

  const renderRow = (option: MultiSelectMenuOption) => {
    const isSelected = selected.has(option.value);
    const Icon = option.icon;
    return (
      <CommandItem
        key={option.value}
        value={option.label}
        onSelect={() => onToggle(option.value)}
        data-checked={isSelected || undefined}
        className="cursor-pointer gap-2"
      >
        {/* Presentational: the row is the control. */}
        <Checkbox
          checked={isSelected}
          tabIndex={-1}
          aria-hidden
          className="pointer-events-none"
        />
        {option.avatar ? (
          <span className="shrink-0">{option.avatar}</span>
        ) : (
          Icon && <Icon className="size-4 shrink-0 text-icon-secondary" />
        )}
        <span className="truncate" title={option.label}>
          {option.label}
        </span>
      </CommandItem>
    );
  };

  const count = selectedValues.length;

  // The footer sits outside the Command root: cmdk claims Enter there for
  // the active row, which would swallow the footer buttons' Enter.
  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <Command
        ref={commandRef}
        tabIndex={showSearch ? undefined : -1}
        className="min-h-0 outline-none"
        onKeyDown={(event) => {
          if (showSearch || event.key !== " ") return;
          const active = commandRef.current?.querySelector<HTMLElement>(
            '[cmdk-item][aria-selected="true"]',
          );
          if (!active) return;
          event.preventDefault();
          active.click();
        }}
      >
        {showSearch && (
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={(term) => {
              setSearch(term);
              onSearchChange?.(term);
            }}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !event.currentTarget.value) {
                onRemoveLast?.();
              }
            }}
          />
        )}
        <CommandList className="max-h-[272px] min-h-0">
          {!isLoading && <CommandEmpty>No results</CommandEmpty>}
          {pinnedOptions.length > 0 && (
            <>
              <CommandGroup heading="Selected">
                {pinnedOptions.map(renderRow)}
              </CommandGroup>
              {restOptions.length > 0 && (
                <CommandSeparator className="mx-0 mb-0" />
              )}
            </>
          )}
          {restOptions.length > 0 && (
            <CommandGroup>{restOptions.map(renderRow)}</CommandGroup>
          )}
          {isLoading && (
            <div className="flex items-center justify-center p-4">
              <Spinner size={16} />
            </div>
          )}
        </CommandList>
      </Command>
      {!hideFooter && (
        <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-t border-border-primary pr-1.5 pl-3">
          <span className="truncate text-xs-v4 text-text-tertiary">
            {count > 0
              ? `${count} selected`
              : `${options.length} ${options.length === 1 ? "option" : "options"}`}
          </span>
          {count > 0 ? (
            <Button variant="ghost" size="sm" onClick={onClear}>
              Clear
            </Button>
          ) : (
            // Hidden while searching: it would also pick the filtered-out
            // options.
            !search &&
            onSelectAll &&
            options.length > 0 && (
              <Button variant="ghost" size="sm" onClick={onSelectAll}>
                Select all
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelectMenu;
